/**
 * Minimal toast notifications (Phase R.3).
 *
 * A process-level singleton store + a single <ToastHost/> mounted once in App,
 * so any component (including the side panel's dispatch buttons) can fire a toast
 * via `showToast(...)` without prop-drilling a context. Toasts auto-dismiss after
 * a timeout and stack bottom-right. Kept dependency-free (no portal needed — the
 * host is fixed-position) and tiny; this is operator feedback, not a design system.
 */
import { useSyncExternalStore } from 'react';

export type ToastVariant = 'success' | 'error' | 'info';

export interface Toast {
  id: number;
  message: string;
  variant: ToastVariant;
}

const DEFAULT_DURATION_MS = 5000;

let toasts: Toast[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot(): Toast[] {
  return toasts;
}

/** Fire a toast. Returns its id (so a caller could dismiss it early). */
export function showToast(
  message: string,
  opts: { variant?: ToastVariant; durationMs?: number } = {},
): number {
  const id = nextId++;
  toasts = [...toasts, { id, message, variant: opts.variant ?? 'info' }];
  emit();
  const ms = opts.durationMs ?? DEFAULT_DURATION_MS;
  if (typeof window !== 'undefined' && ms > 0) {
    window.setTimeout(() => dismissToast(id), ms);
  }
  return id;
}

export function dismissToast(id: number): void {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

/** Test seam: clear the store between cases. */
export function __resetToasts(): void {
  toasts = [];
  nextId = 1;
}

const VARIANT_CLASS: Record<ToastVariant, string> = {
  success: 'border-emerald-500/50 bg-emerald-950/90 text-emerald-100',
  error: 'border-rose-500/50 bg-rose-950/90 text-rose-100',
  info: 'border-slate-600 bg-slate-900/95 text-slate-100',
};

/** Mount once (App). Renders the active toast stack bottom-right. */
export function ToastHost(): JSX.Element {
  const items = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return (
    <div
      data-testid="toast-host"
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none"
      aria-live="polite"
    >
      {items.map((t) => (
        <button
          key={t.id}
          type="button"
          data-testid="toast"
          data-variant={t.variant}
          onClick={() => dismissToast(t.id)}
          className={`pointer-events-auto max-w-sm text-left text-sm px-3 py-2 rounded border shadow-lg ${VARIANT_CLASS[t.variant]}`}
        >
          {t.message}
        </button>
      ))}
    </div>
  );
}
